import { FunctionComponent, useCallback, useState, MouseEvent } from 'react'
import clsx from 'clsx'
import {
  useWidgetContext,
  Translation,
  usePlatformConfig,
} from '@dxp-module/common'
import { MyAccountTheme } from '@dxp-module/themes'
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
  Typography,
} from '@mui/material'
import {
  ManageAccounts,
  Logout,
  ChevronLeft,
  Person,
} from '@mui/icons-material'
import { Image } from '@dxp-module/mui-image'
import useDropdownOptions from '../../hooks/useDropdownOptions'
import { DropdownOption } from '../../model/DropdownOption'
import useImageFromAssets from '../../../common/hooks/useImageFromAssets'
import miniWidgetDropdownButtonStyles from './miniWidgetDropdownButton.styles'

type MiniWidgetDropdownButtonProps = {
  isLoggedIn: boolean
  welcomeMessage?: string
}

const MiniWidgetDropdownButton: FunctionComponent<
  MiniWidgetDropdownButtonProps
> = ({ isLoggedIn, welcomeMessage }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const styles = miniWidgetDropdownButtonStyles({})
  const { defaultOptionList, handleAction } = useDropdownOptions()
  const { breakpoints } = useTheme<MyAccountTheme>()
  const isMobile = useMediaQuery(breakpoints.down('sm'))
  const isOpen = Boolean(anchorEl)

  const {
    configuration: { myAccountUrl },
  } = useWidgetContext()

  const {
    platformConfig: { myAccount },
  } = usePlatformConfig()
  const { loggedInOptions, loggedOutOptions, withWelcomeMessage } =
    myAccount?.miniMyAccountConfig?.dropdownOptions || {}

  const useDropdown = isLoggedIn || !!loggedOutOptions
  const optionList = myAccount?.miniMyAccountConfig?.dropdownOptions
    ? ((isLoggedIn ? loggedInOptions : loggedOutOptions) as DropdownOption[])
    : defaultOptionList

  const { features } = useTheme<MyAccountTheme>()
  const { displayMiniDropdownOnLeft, useMiniCustomDropdownIcon } =
    features ?? {}

  const icons = {
    ManageAccounts: ManageAccounts,
    Logout: Logout,
  }

  const menuIcon = useImageFromAssets(
    `MiniMyAccountIcon${isLoggedIn ? 'LoggedIn' : 'LoggedOut'}`
  )

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (useDropdown) {
        return setAnchorEl(event.currentTarget)
      }

      return (window.location.href = myAccountUrl || '')
    },
    [isLoggedIn, myAccountUrl]
  )

  return (
    <>
      <IconButton
        id={'mini-my-account-dropdown-button'}
        disableRipple={true}
        className={clsx(styles.iconButton, {
          [styles.loggedInIconButton]: isLoggedIn,
          [styles.loggedOutIconButton]: !isLoggedIn,
          [styles.menuOpenIconButton]: isOpen,
        })}
        onClick={handleClick}
        data-test={'mini:myaccount:button'}
      >
        {useMiniCustomDropdownIcon ? (
          <Image
            src={menuIcon}
            disableTransition={true}
            animationDuration={0}
            alt="Menu Icon"
            style={{
              objectFit: 'contain',
              minHeight: '50px',
              minWidth: '50px',
            }}
          />
        ) : !isMobile ? (
          <ChevronLeft
            className={clsx([
              styles.icon,
              {
                [styles.iconOpened]: isOpen,
                [styles.iconClosed]: !isOpen,
              },
            ])}
            data-test={`mini:myaccount:signed:${isLoggedIn ? 'in' : 'out'}`}
          />
        ) : (
          <Person
            className={styles.icon}
            data-test={`mini:myaccount:signed:${isLoggedIn ? 'in' : 'out'}`}
          />
        )}
      </IconButton>
      {useDropdown && (
        <Menu
          id="mini-my-account-dropdown"
          anchorEl={anchorEl}
          open={isOpen}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: displayMiniDropdownOnLeft ? 'right' : 'left',
          }}
          transformOrigin={
            displayMiniDropdownOnLeft
              ? { horizontal: 'right', vertical: 'top' }
              : undefined
          }
          onClose={handleClose}
          PopoverClasses={{
            paper: styles.popover,
          }}
          MenuListProps={{
            classes: {
              root: styles.menu,
            },
            'aria-labelledby': 'mini-my-account-dropdown-button',
          }}
          data-test={'mini:myaccount:dropdown:menu'}
        >
          {(isMobile || !!withWelcomeMessage) && isLoggedIn && (
            <>
              <MenuItem
                classes={{
                  root: styles.menuItem,
                }}
                data-test={'mini:myaccount:dropdown:welcome'}
              >
                <ListItemText>
                  <Typography
                    component="span"
                    dangerouslySetInnerHTML={{ __html: welcomeMessage || '' }}
                    className={styles.welcomeMessage}
                  />
                </ListItemText>
              </MenuItem>
              <Divider className={styles.divider} />
            </>
          )}
          {optionList.map(({ label, key, iconName }) => {
            const Icon = icons[iconName as keyof typeof icons]
            return (
              <MenuItem
                classes={{
                  root: styles.menuItem,
                }}
                key={key}
                onClick={() => handleAction(key)}
                data-test={`mini:myaccount:dropdown:menu:item:${key}`}
              >
                {iconName && (
                  <ListItemIcon>
                    <Icon className={styles.listIcon} />
                  </ListItemIcon>
                )}
                <ListItemText>
                  <Translation id={label} className={styles.listLabel} />
                </ListItemText>
              </MenuItem>
            )
          })}
        </Menu>
      )}
    </>
  )
}

export default MiniWidgetDropdownButton
